// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyContract, TyInstallmentPlan, TyReservation } from "@/types";
import DealCard from "@/UI/cards/DealCard";
import PlanCard from "@/UI/cards/PlanCard";
import EmptyState from "@/UI/components/EmptyState";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import DealPopup, { type TyDeal } from "@/UI/popups/DealPopup";
import PaymentFormPopup from "@/UI/popups/PaymentFormPopup";
import PlanTimelinePopup from "@/UI/popups/PlanTimelinePopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    customerId: string;
    /**
     * Which half. **A hold is not a contract**, and a person's page says so by giving each its own
     * tab: a contract means they have the flat, a reservation means they are still deciding, and a
     * list that ran the two together answered "what do they own" with a maybe.
     */
    show: "contracts" | "reservations";
    /** Bumped by the screen when something it owns changed, so the lists reload with it. */
    reloadAt?: number | null;
};

/**
 * Everything one person has signed, or everything they are holding — never both at once.
 *
 * **It belongs to the person, not to the unit.** A unit's own screen used to mount this too, and
 * what it showed was at most one live contract and one live hold, both of which its status badge
 * already names; what it buried underneath them was the cancelled history of a flat, which is a
 * question about the people who walked away rather than about the flat. So the unit's badge opens
 * the live paper and this list stays here, where the cancelled rows belong to somebody.
 *
 * Cancelled rows are included rather than hidden. A person who has reserved and let go of three
 * units is exactly the person whose history somebody needs before offering a fourth.
 *
 * **The money rides with the contracts.** A payment plan belongs to the same question as the paper
 * that created it — "what does this person owe" — and sending somebody to the CRM's installments
 * tab to find out would be a navigation away from the record and back. The plans are shown whole
 * rather than row by row: that list is a company-wide question and it already has a screen.
 */
export default function RecordDeals({ customerId, show, reloadAt }: Props) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);

    const contracts = useFetch<{ contracts: TyContract[] }>("/sales/contracts", {}, false);
    const reservations = useFetch<{ reservations: TyReservation[] }>("/sales/reservations", {}, false);
    const plans = useFetch<{ plans: TyInstallmentPlan[] }>("/sales/plans", {}, false);
    const action = useFetch("/sales", {}, false);

    const isPapers = show === "contracts";

    const load = () => {
        if (!customerId) return;
        const query = { customer: customerId, live: "all", limit: constants.maxPageSize };
        if (isPapers) {
            contracts.invoke({ query });
            plans.invoke({ query: { customer: customerId, limit: constants.maxPageSize } });
        } else reservations.invoke({ query });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, show, reloadAt, action.succeededAt]);

    const confirmCancel = (kind: "contract" | "reservation", id: string) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: kind === "contract" ? t.sales.cancelContract : t.sales.cancelReservation,
                message: kind === "contract" ? t.sales.confirmCancelContract : t.sales.confirmCancelReservation,
                confirmLabel: kind === "contract" ? t.sales.cancelContract : t.sales.cancelReservation,
                onConfirm: () => action.invoke({ method: "PATCH", path: `/sales/${kind}s/${id}/cancel`, data: { reason: "other" } }),
            },
        });

    const openPayment = (planId: string, suggested: number | null) =>
        addComp({ name: "payment-form", Component: PaymentFormPopup, anim: "scale", details: { planId, suggested, onDone: load } });

    /**
     * The paper, read whole — and the only way to cancel one, from the menu inside it. A card in a
     * grid carries no destructive button: the thumb that opens it is the thumb that would press it.
     */
    const openDeal = (deal: TyDeal) =>
        addComp({
            name: "deal",
            Component: DealPopup,
            anim: "scale",
            details: { deal, onCancel: () => confirmCancel(deal.kind, deal._id) },
        });

    /** The same timeline the installments screen opens — a plan reads the same wherever it is met. */
    const openSchedule = (plan: TyInstallmentPlan) =>
        addComp({ name: "plan-timeline", Component: PlanTimelinePopup, place: "end", anim: "endSlide", details: { plan } });

    const papers = contracts.data?.contracts ?? [];
    const holds = reservations.data?.reservations ?? [];
    const schedules = plans.data?.plans ?? [];
    const isEmpty = isPapers ? !papers.length && !schedules.length : !holds.length;
    const isLoading = isPapers ? contracts.loading || plans.loading : reservations.loading;

    return (
        <div className="record-deals">
            {action.error && <p className="record-deals__state record-deals__state--error">{action.error}</p>}
            {isLoading && isEmpty && <p className="record-deals__state">{t.common.loading}</p>}
            {!isLoading && isEmpty && (
                <EmptyState
                    icon={isPapers ? "document" : "history"}
                    title={isPapers ? t.sales.emptyContracts : t.sales.emptyReservations}
                    description={isPapers ? t.sales.emptyContractsHint : t.sales.emptyReservationsHint}
                />
            )}

            {papers.length > 0 && (
                <section className="record-deals__group">
                    <h2 className="record-deals__title">{t.sales.contracts}</h2>
                    <div className="record-deals__grid">
                        {papers.map((contract) => (
                            <DealCard
                                key={contract._id}
                                deal={{ kind: "contract", ...contract }}
                                hidePerson
                                onOpen={() => openDeal({ kind: "contract", ...contract })}
                            />
                        ))}
                    </div>
                </section>
            )}

            {schedules.length > 0 && (
                <section className="record-deals__group">
                    <h2 className="record-deals__title">{t.sales.plans}</h2>
                    <div className="record-deals__grid">
                        {schedules.map((plan) => (
                            <PlanCard
                                key={plan._id}
                                plan={plan}
                                hidePerson
                                onSchedule={() => openSchedule(plan)}
                                onPay={() => openPayment(plan._id, Math.max((plan.totalAmount ?? 0) - (plan.paidAmount ?? 0), 0))}
                            />
                        ))}
                    </div>
                </section>
            )}

            {holds.length > 0 && (
                <div className="record-deals__grid">
                    {holds.map((reservation) => (
                        <DealCard
                            key={reservation._id}
                            deal={{ kind: "reservation", ...reservation }}
                            hidePerson
                            onOpen={() => openDeal({ kind: "reservation", ...reservation })}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
